import { hasLocalStorage } from './ssr';
import { generateEventId } from './ids';
import type { EnrichedEvent, RetryBatch, DeadLetterBatch } from './types';

const STORAGE_KEY = '@entourage/ecs-telemetry-module:retry-queue';
const MAX_BYTES = 2 * 1024 * 1024;
const MAX_RETRIES = 5;
const MAX_DEBUG_EVENTS = 500;
const BACKOFF_MS = [1000, 4000, 16000, 60000, 60000];

interface QueueState {
  batches: RetryBatch[];
  deadLetter: DeadLetterBatch[];
  // Debug-mode capture. Present only when initEventTracker was called with
  // `debug: true`. Local QA can inspect this array from DevTools →
  // Application → Local Storage on the same key as batches/deadLetter.
  debugEvents?: EnrichedEvent[];
}

const emptyState = (): QueueState => ({ batches: [], deadLetter: [] });

const isValidState = (v: unknown): v is QueueState =>
  !!v && typeof v === 'object' && Array.isArray((v as QueueState).batches)
    && Array.isArray((v as QueueState).deadLetter);

// In-memory cache: one parse per page load instead of one per operation. A
// typical replay cycle hits this 6-10 times in quick succession (one for replay,
// one per markSucceeded, two for getDiagnostics). Invalidated by other-tab
// writes via the `storage` event (see invalidateCache below + tracker.ts wires
// the listener).
let cachedState: QueueState | null = null;

export const invalidateCache = (): void => {
  cachedState = null;
};

// Corruption can come from partial writes, quota evictions mid-write, or a foreign
// script reusing the key. Self-heal visibly: log + remove the corrupt key so the
// next enqueue starts clean. Silent reset hid the original bug for 30 minutes.
const readState = (): QueueState => {
  if (cachedState) return cachedState;
  if (!hasLocalStorage()) {
    cachedState = emptyState();
    return cachedState;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      cachedState = emptyState();
      return cachedState;
    }
    const parsed = JSON.parse(raw);
    if (!isValidState(parsed)) {
      console.warn('[ecs-telemetry] retry queue corrupt (shape mismatch); resetting');
      localStorage.removeItem(STORAGE_KEY);
      cachedState = emptyState();
      return cachedState;
    }
    cachedState = parsed;
    return parsed;
  } catch (err) {
    console.warn('[ecs-telemetry] retry queue unreadable; resetting', err);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    cachedState = emptyState();
    return cachedState;
  }
};

// Eviction priority on cap overflow (2 MB):
//   1. debugEvents — dev-only data, fully discardable.
//   2. batches    — active retries; an oldest active batch is recoverable in
//                   principle (the network may succeed next time).
//   3. deadLetter — forensic audit trail of permanent failures; preserved last
//                   because operators need it most when debugging a stuck queue.
//
// We clone the input state's arrays before mutating, so an in-flight reader
// holding the prior cachedState reference doesn't see a half-evicted array.
// The cache is updated AFTER a successful localStorage write, so a write
// failure leaves the in-memory state aligned with what's actually persisted.
const writeState = (state: QueueState): void => {
  if (!hasLocalStorage()) {
    cachedState = state;
    return;
  }
  const work: QueueState = {
    batches: [...state.batches],
    deadLetter: [...state.deadLetter],
    ...(state.debugEvents ? { debugEvents: [...state.debugEvents] } : {}),
  };
  let serialized = JSON.stringify(work);
  while (serialized.length > MAX_BYTES) {
    if (work.debugEvents && work.debugEvents.length > 0) {
      work.debugEvents.shift();
    } else if (work.batches.length > 0) {
      const dropped = work.batches.shift()!;
      console.warn(
        '[ecs-telemetry] retry queue cap exceeded, dropping oldest active batch',
        dropped.batchId,
      );
    } else if (work.deadLetter.length > 0) {
      work.deadLetter.shift();
    } else {
      cachedState = work;
      return;
    }
    serialized = JSON.stringify(work);
  }
  try {
    localStorage.setItem(STORAGE_KEY, serialized);
    cachedState = work;
  } catch (err) {
    console.warn('[ecs-telemetry] localStorage write failed', err);
    // cachedState left untouched — what's in memory still matches the prior
    // successful write to localStorage.
  }
};

const jitter = (delay: number): number => delay * (0.75 + Math.random() * 0.5);

export const enqueue = (batch: RetryBatch): void => {
  if (!hasLocalStorage()) return;
  const state = readState();
  state.batches.push(batch);
  writeState(state);
};

export const replay = (): RetryBatch[] => {
  if (!hasLocalStorage()) return [];
  const state = readState();
  const now = Date.now();
  const ready: RetryBatch[] = [];
  for (const batch of state.batches) {
    const nextDelay = BACKOFF_MS[Math.min(batch.retryCount, BACKOFF_MS.length - 1)];
    const earliestRetry = batch.lastAttemptAt + jitter(nextDelay);
    if (now >= earliestRetry) ready.push(batch);
  }
  return ready;
};

export const markFailed = (batchId: string): void => {
  if (!hasLocalStorage()) return;
  const state = readState();
  const idx = state.batches.findIndex((b) => b.batchId === batchId);
  if (idx === -1) return;
  const batch = state.batches[idx];
  batch.retryCount += 1;
  batch.lastAttemptAt = Date.now();
  if (batch.retryCount > MAX_RETRIES) {
    state.batches.splice(idx, 1);
    state.deadLetter.push({
      batchId: batch.batchId,
      events: batch.events,
      droppedAt: Date.now(),
      reason: `Exceeded ${MAX_RETRIES} retries`,
    });
  }
  writeState(state);
};

export const markSucceeded = (batchId: string): void => {
  if (!hasLocalStorage()) return;
  const state = readState();
  state.batches = state.batches.filter((b) => b.batchId !== batchId);
  writeState(state);
};

export const clear = (): void => {
  cachedState = null;
  if (!hasLocalStorage()) return;
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
};

// Merged accessor: a single readState() round trip instead of two when
// getDiagnostics calls both. Existing single-purpose exports kept for any
// external caller, but tracker.getDiagnostics now uses getCounts.
export const getCounts = (): { active: number; deadLetter: number } => {
  if (!hasLocalStorage()) return { active: 0, deadLetter: 0 };
  const state = readState();
  return { active: state.batches.length, deadLetter: state.deadLetter.length };
};

export const getActiveCount = (): number => getCounts().active;
export const getDeadLetterCount = (): number => getCounts().deadLetter;

// `lastAttemptAt: 0` is intentional and differs from the catch-path enqueue in
// tracker.ts (which uses Date.now()). Beforeunload-persisted batches never got
// a chance to attempt upload, so they should be eligible for immediate replay
// on the next page load — not gated behind a backoff window.
export const flushPendingSync = (events: EnrichedEvent[]): void => {
  if (!hasLocalStorage() || events.length === 0) return;
  enqueue({
    batchId: generateEventId(),
    events,
    retryCount: 0,
    lastAttemptAt: 0,
  });
};

// Debug-mode sink: appends to state.debugEvents in the same localStorage key
// used by batches/deadLetter. One key to inspect in DevTools, three arrays
// inside. FIFO trim to MAX_DEBUG_EVENTS (soft cap) before the writeState cap
// kicks in.
export const pushDebugEvent = (event: EnrichedEvent): void => {
  if (!hasLocalStorage()) return;
  const state = readState();
  const debugEvents = state.debugEvents ?? [];
  debugEvents.push(event);
  if (debugEvents.length > MAX_DEBUG_EVENTS) {
    debugEvents.splice(0, debugEvents.length - MAX_DEBUG_EVENTS);
  }
  writeState({ ...state, debugEvents });
};

export const clearDebugEvents = (): void => {
  if (!hasLocalStorage()) return;
  const state = readState();
  if (!state.debugEvents) return;
  const { debugEvents, ...rest } = state;
  writeState(rest);
};

export const getDebugEventCount = (): number => {
  if (!hasLocalStorage()) return 0;
  return readState().debugEvents?.length ?? 0;
};
